import dayjs from 'dayjs'

import type { TDateInput, TDayjsDateInput } from './date-picker.types'

export const toValidDate = (value?: TDateInput | null): Date | null => {
  if (value === null || value === undefined) {
    return null
  }

  const parsedValue =
    typeof value === 'object' && 'valueOf' in value && typeof value.valueOf === 'function' ? value.valueOf() : value

  const parsedDate = dayjs(parsedValue as TDayjsDateInput)

  if (!parsedDate.isValid()) {
    return null
  }

  return parsedDate.toDate()
}

/**
 * Whether `day` falls outside the inclusive `[minDate, maxDate]` window, compared
 * by calendar day. `maxDate` defaults to today. Shared by every upload-date
 * filter's `isOutsideRange`/`excludeDate` predicate — only the `minDate` (the
 * lookback window) varies per context, so callers compute that and pass it in.
 */
export const isDayOutsideRange = (
  day: TDateInput,
  minDate: TDayjsDateInput,
  maxDate: TDayjsDateInput = new Date()
): boolean => {
  const parsedDay = toValidDate(day)

  if (!parsedDay) {
    return true
  }

  const target = dayjs(parsedDay).startOf('day')

  return target.isAfter(dayjs(maxDate).startOf('day')) || target.isBefore(dayjs(minDate).startOf('day'))
}

/**
 * Whether `day` falls outside the current calendar year (from Jan 1st up to
 * today). Pass it as an `isOutsideRange` predicate to restrict a date picker to
 * the current year. The year is resolved at call time, so it's never hardcoded.
 */
export const isOutsideCurrentYear = (day: TDateInput): boolean =>
  isDayOutsideRange(day, dayjs().startOf('year').toDate())
